/**
 * Licitações (17 §3.2): cadastro (manual ou pelo PNCP — D70) e ficha em abas (Dados, Itens, Precificação, Arquivos,
 * Linha do tempo). Lista e painel em licitacao-lista.tsx; status em licitacao-status.tsx (D86).
 */
import { useEffect, useMemo, useState } from "react";
import { useWatch } from "react-hook-form";
import {
  AutocompleteInput,
  BooleanInput,
  Create,
  DateInput,
  DateTimeInput,
  Edit,
  Loading,
  NumberInput,
  ReferenceInput,
  SelectInput,
  SimpleForm,
  TextInput,
  Title,
  required,
  useEditContext,
  useGetList,
  useGetOne,
  useNotify,
} from "react-admin";
import { useNavigate } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextFieldMui from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import SearchIcon from "@mui/icons-material/ManageSearchOutlined";
import { beneficioDoPncp, lerControlePncp, type ControlePncp } from "@sobral/dominio";
import { BarraFormulario, EntradaListaCrescente, vazioParaNulo } from "../componentes";
import { formatarDataHora, normalizarBusca } from "../lib/formato";
import {
  ESFERA_PNCP,
  consultarArquivos,
  consultarCompra,
  consultarItens,
  dataHoraPncp,
  linkPncp,
  tipoDeArquivo,
  type CompraPncp,
  type ItemPncp,
} from "../lib/pncp";
import { supabase } from "../lib/supabase";
import { UFS } from "./fornecedores";
import { CadastroRapidoPessoa, criarPessoaPorCnpj } from "./orgaos";
import { ItensLicitacao } from "./licitacao-itens";
import { ArquivosLicitacao } from "./licitacao-arquivos";
import { PrecificacaoLicitacao, type LicitacaoPreco } from "./licitacao-precificacao";
import { AcaoDaEtapa, SeletorStatus, nomeStatus } from "./licitacao-status";
import { SelosLicitacao } from "./licitacao-resultado";
import { prepararCaptura, textoDaPreparacao, type ResumoPreparacao } from "./licitacao-leitura";
import { DisputaLicitacao } from "./licitacao-disputa";
import { PropostaLicitacao } from "./licitacao-proposta";
import { PastaOneDrive, sincronizarOneDrive } from "./onedrive";

/* ------------------------------------ Campos (Dados) ------------------------------------ */

const ESFERAS = [
  { id: "F", name: "Federal" },
  { id: "E", name: "Estadual" },
  { id: "M", name: "Municipal" },
  { id: "D", name: "Distrital" },
];

function EntradaUnidade() {
  const pessoaId = useWatch({ name: "pessoa_id" }) as string | undefined;
  if (!pessoaId) {
    return <TextFieldMui label="Unidade compradora" disabled fullWidth size="small" helperText="Escolha o órgão primeiro." sx={{ mt: 1, mb: 0.5 }} />;
  }
  return (
    <ReferenceInput source="unidade_compradora_id" reference="unidade_compradora" filter={{ pessoa_id: pessoaId }} perPage={100}>
      <SelectInput
        optionText={(u: { codigo: string | null; nome: string }) => (u.codigo ? `${u.codigo} — ${u.nome}` : u.nome)}
        emptyText="Não informada"
        parse={vazioParaNulo}
        fullWidth
        helperText="Cadastre as unidades (UASG) na ficha do órgão."
      />
    </ReferenceInput>
  );
}

/** "2026-10-25T09:00" digitado no horário do navegador → ISO com fuso (o banco guarda timestamptz). */
const dataHoraLocalParaIso = (valor: string | null | undefined) => {
  if (!valor) return null;
  const d = new Date(valor);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

function EntradaSessao() {
  const sessao = useWatch({ name: "data_hora_sessao" }) as string | null | undefined;
  const fim = useWatch({ name: "fim_recebimento_propostas" }) as string | null | undefined;
  const sugerida = Boolean(sessao && fim && new Date(sessao).getTime() === new Date(fim).getTime());
  return (
    <DateTimeInput
      source="data_hora_sessao"
      parse={dataHoraLocalParaIso}
      fullWidth
      helperText={sugerida ? "Sugerida pelo fim do recebimento de propostas publicado no PNCP. Confira no edital." : " "}
    />
  );
}

function PeriodoPncp() {
  const inicio = useWatch({ name: "inicio_recebimento_propostas" }) as string | null | undefined;
  const fim = useWatch({ name: "fim_recebimento_propostas" }) as string | null | undefined;
  if (!inicio && !fim) return null;
  return (
    <Typography variant="caption" color="text.secondary">
      Recebimento de propostas no PNCP: {formatarDataHora(inicio)} a {formatarDataHora(fim)}
    </Typography>
  );
}

function CamposLicitacao() {
  return (
    <Grid container columnSpacing={2} sx={{ width: "100%" }}>
      <Grid size={{ xs: 12, md: 6 }}>
        <ReferenceInput source="pessoa_id" reference="pessoa" sort={{ field: "nome", order: "ASC" }} perPage={25}>
          <AutocompleteInput
            optionText="nome"
            validate={required()}
            fullWidth
            filterToQuery={(t: string) => (t ? { "busca@ilike": normalizarBusca(t) } : {})}
            create={<CadastroRapidoPessoa />}
            createItemLabel='Cadastrar órgão "%{item}"'
            createLabel="Cadastrar novo órgão"
          />
        </ReferenceInput>
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <EntradaUnidade />
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <TextInput source="numero" validate={required()} fullWidth helperText="Ex.: 90001/2025" />
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <TextInput source="processo" parse={vazioParaNulo} fullWidth />
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <EntradaListaCrescente source="modalidade_id" reference="modalidade" rotuloNovo="Modalidade" label="Modalidade" />
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <ReferenceInput source="portal_id" reference="portal" filter={{ ativo: true }} sort={{ field: "nome", order: "ASC" }}>
          <SelectInput optionText="nome" emptyText="Não informado" parse={vazioParaNulo} fullWidth />
        </ReferenceInput>
      </Grid>
      <Grid size={12}>
        <TextInput source="objeto" validate={required()} multiline fullWidth />
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
        <EntradaSessao />
      </Grid>
      <Grid size={{ xs: 6, md: 2 }}>
        <SelectInput source="uf_destino" choices={UFS} emptyText="—" parse={vazioParaNulo} fullWidth />
      </Grid>
      <Grid size={{ xs: 6, md: 2 }}>
        <SelectInput source="esfera" choices={ESFERAS} emptyText="—" parse={vazioParaNulo} fullWidth helperText="Do órgão" />
      </Grid>
      <Grid size={{ xs: 6, md: 3 }}>
        <DateInput source="prazo_impugnacao" parse={vazioParaNulo} fullWidth />
      </Grid>
      <Grid size={{ xs: 6, md: 3 }}>
        <DateInput source="prazo_esclarecimento" parse={vazioParaNulo} fullWidth />
      </Grid>
      <Grid size={12} sx={{ mb: 1 }}>
        <PeriodoPncp />
      </Grid>
      <Grid size={{ xs: 12, sm: 4 }}>
        <NumberInput source="vigencia_ata_meses" min={1} parse={(v: string) => (v === "" ? null : Number(v))} fullWidth helperText="Conforme o edital" />
      </Grid>
      <Grid size={{ xs: 6, sm: 4 }}>
        <NumberInput source="prazo_entrega_dias" min={0} parse={(v: string) => (v === "" ? null : Number(v))} fullWidth helperText="Conforme o edital" />
      </Grid>
      <Grid size={{ xs: 6, sm: 4 }}>
        <NumberInput source="prazo_pagamento_dias" min={0} parse={(v: string) => (v === "" ? null : Number(v))} fullWidth helperText="Conforme o edital" />
      </Grid>
      <Grid size={{ xs: 12, sm: 4 }}>
        <BooleanInput
          source="srp"
          helperText="Registro de preços: o órgão registra o preço em ata para compras futuras e não é obrigado a comprar tudo (Lei 14.133/2021, art. 6º, XLV, e art. 83)."
        />
      </Grid>
      <Grid size={{ xs: 6, sm: 4 }}>
        <NumberInput source="carona_limite_aderente_pct" min={0} validate={required()} fullWidth helperText="Padrão: 50%" />
      </Grid>
      <Grid size={{ xs: 6, sm: 4 }}>
        <NumberInput source="carona_limite_total_pct" min={0} validate={required()} fullWidth helperText="Padrão: 200%" />
      </Grid>
      <Grid size={{ xs: 12, sm: 4 }}>
        <BooleanInput
          source="margem_preferencia"
          helperText="Marque se o edital prevê margem de preferência (confira os termos no edital). Em órgão estadual de SE, ela vem da Lei SE 8.747/2020, art. 3º: até 10% sobre a melhor proposta para ME/EPP sediada em SE."
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 4 }}>
        <ReferenceInput source="responsavel_id" reference="usuario" filter={{ ativo: true }} sort={{ field: "nome", order: "ASC" }}>
          <SelectInput optionText="nome" emptyText="Não definido" parse={vazioParaNulo} fullWidth />
        </ReferenceInput>
      </Grid>
      <Grid size={{ xs: 12, sm: 4 }}>
        <TextInput source="pasta_url" parse={vazioParaNulo} fullWidth type="url" helperText="Pasta no OneDrive, se houver" />
      </Grid>
      <Grid size={12}>
        <TextInput source="observacao" parse={vazioParaNulo} multiline fullWidth />
      </Grid>
    </Grid>
  );
}

/* ------------------------------------ Cadastro pelo PNCP (D70) ------------------------------------ */

interface ConsultaPncp {
  controle: ControlePncp;
  compra: CompraPncp;
  itens: ItemPncp[];
}

async function orgaoDoPncp(compra: CompraPncp): Promise<string> {
  const cnpj = compra.orgaoEntidade.cnpj;
  const { data: existente } = await supabase.from("pessoa").select("id").eq("documento", cnpj).maybeSingle();
  if (existente) return existente.id as string;
  try {
    return (await criarPessoaPorCnpj(cnpj)).id;
  } catch {
    // Receita indisponível: cadastra com os dados publicados no PNCP (completáveis depois na ficha).
    const { data, error } = await supabase
      .from("pessoa")
      .insert({
        tipo: "publico",
        documento: cnpj,
        nome: compra.orgaoEntidade.razaoSocial,
        razao_social: compra.orgaoEntidade.razaoSocial,
        esfera: ESFERA_PNCP[compra.orgaoEntidade.esferaId] ?? null,
        uf: compra.unidadeOrgao.ufSigla || null,
        municipio: compra.unidadeOrgao.municipioNome || null,
      })
      .select("id")
      .single();
    if (error) throw new Error(`Não foi possível cadastrar o órgão: ${error.message}`);
    return data.id as string;
  }
}

async function unidadeDoPncp(compra: CompraPncp, pessoaId: string): Promise<string | null> {
  const codigo = compra.unidadeOrgao.codigoUnidade?.trim();
  if (!codigo || !/^[0-9]{1,12}$/.test(codigo)) return null;
  const { data: existente } = await supabase.from("unidade_compradora").select("id").eq("codigo", codigo).maybeSingle();
  if (existente) return existente.id as string;
  const { data, error } = await supabase
    .from("unidade_compradora")
    .insert({
      pessoa_id: pessoaId,
      codigo,
      nome: compra.unidadeOrgao.nomeUnidade || codigo,
      municipio: compra.unidadeOrgao.municipioNome || null,
      uf: compra.unidadeOrgao.ufSigla || null,
    })
    .select("id")
    .single();
  if (error) throw new Error(`Não foi possível cadastrar a unidade compradora: ${error.message}`);
  return data.id as string;
}

async function modalidadeDoPncp(compra: CompraPncp): Promise<string> {
  const { data } = await supabase.from("modalidade").select("id").contains("codigos_pncp", [compra.modalidadeId]).limit(1);
  if (data?.[0]) return data[0].id as string;
  // Modalidade nova no PNCP: entra na lista que cresce.
  const { data: nova, error } = await supabase
    .from("modalidade")
    .insert({ nome: compra.modalidadeNome, codigos_pncp: [compra.modalidadeId] })
    .select("id")
    .single();
  if (error) throw new Error(`Não foi possível cadastrar a modalidade: ${error.message}`);
  return nova.id as string;
}

/** Portal pelo endereço do sistema de origem publicado no PNCP (só o que é inequívoco). */
async function portalDoPncp(link: string | null, objeto: string): Promise<string | null> {
  let host = "";
  if (link) {
    try {
      host = new URL(link).hostname.toLowerCase();
    } catch {
      host = "";
    }
  }
  // Alguns órgãos publicam o portal no início do objeto: "[LICITANET] - ...".
  const nome =
    host.endsWith("compras.gov.br") || host.endsWith("estaleiro.serpro.gov.br")
      ? "Compras.gov.br"
      : host.endsWith("licitanet.com.br") || /^\s*\[licitanet\]/i.test(objeto)
        ? "Licitanet"
        : null;
  if (!nome) return null;
  const { data } = await supabase.from("portal").select("id").eq("nome", nome).maybeSingle();
  return (data?.id as string | undefined) ?? null;
}

function itemDoPncp(licitacaoId: string, i: ItemPncp) {
  const ncm = (i.ncmNbsCodigo ?? "").replace(/\D/g, "");
  return {
    licitacao_id: licitacaoId,
    numero: i.numeroItem,
    descricao: i.descricao.trim(),
    qtd_max: i.quantidade,
    unidade: i.unidadeMedida?.trim() || null,
    valor_unit_estimado: i.orcamentoSigiloso ? null : i.valorUnitarioEstimado,
    beneficio_me_epp: beneficioDoPncp(i.tipoBeneficio),
    ncm: ncm.length >= 2 && ncm.length <= 8 ? ncm : null,
    situacao_pncp: i.situacaoCompraItemNome,
    observacao: i.informacaoComplementar?.trim() || null,
  };
}

/** Grava a licitação e os itens a partir dos dados consultados no PNCP (órgão, unidade e modalidade criados se faltarem). */
export async function gravarLicitacaoDoPncp(c: ControlePncp, compra: CompraPncp, itens: ItemPncp[]): Promise<string> {
  const pessoaId = await orgaoDoPncp(compra);
  const [unidadeId, modalidadeId, portalId] = await Promise.all([
    unidadeDoPncp(compra, pessoaId),
    modalidadeDoPncp(compra),
    portalDoPncp(compra.linkSistemaOrigem, compra.objetoCompra),
  ]);
  const fim = dataHoraPncp(compra.dataEncerramentoProposta);
  const { data: licitacao, error } = await supabase
    .from("licitacao")
    .insert({
      pessoa_id: pessoaId,
      unidade_compradora_id: unidadeId,
      portal_id: portalId,
      numero_controle_pncp: c.numero,
      numero: `${compra.numeroCompra}/${compra.anoCompra}`,
      processo: compra.processo?.trim() || null,
      modalidade_id: modalidadeId,
      srp: compra.srp,
      esfera: /^[FEMD]$/.test(compra.orgaoEntidade.esferaId ?? "") ? compra.orgaoEntidade.esferaId : null,
      objeto: compra.objetoCompra.trim(),
      uf_destino: compra.unidadeOrgao.ufSigla || null,
      inicio_recebimento_propostas: dataHoraPncp(compra.dataAberturaProposta),
      fim_recebimento_propostas: fim,
      data_hora_sessao: fim,
      link_sistema_origem: compra.linkSistemaOrigem?.startsWith("http") ? compra.linkSistemaOrigem : null,
    })
    .select("id")
    .single();
  if (error) throw new Error(error.message);
  const id = licitacao.id as string;
  if (itens.length > 0) {
    const { error: erroItens } = await supabase.from("licitacao_item").insert(itens.map((i) => itemDoPncp(id, i)));
    if (erroItens) throw new Error(`Licitação cadastrada, mas os itens falharam: ${erroItens.message}`);
  }
  return id;
}

/** Depois de gravar (D99): baixa os arquivos do PNCP, lê o edital e preenche o que estiver vazio. Falha aqui não desfaz a captação. */
export async function prepararDoPncp(licitacaoId: string, c: ControlePncp, aviso: (texto: string) => void): Promise<ResumoPreparacao | null> {
  try {
    const arquivos = await consultarArquivos(c);
    return await prepararCaptura(
      licitacaoId,
      arquivos.map((a) => ({ url: a.url, titulo: a.titulo, tipo: tipoDeArquivo(`${a.tipoDocumentoNome} ${a.titulo}`) })),
      aviso,
    );
  } catch {
    return null;
  }
}

/** Cadastra pelo número de controle (busca e novas oportunidades), já com arquivos e leitura do edital. */
export async function cadastrarDoPncp(
  c: ControlePncp,
  aviso: (texto: string) => void = () => undefined,
): Promise<{ id: string; itens: number; existente: boolean; preparacao: ResumoPreparacao | null }> {
  const { data: existente } = await supabase.from("licitacao").select("id").eq("numero_controle_pncp", c.numero).maybeSingle();
  if (existente) return { id: existente.id as string, itens: 0, existente: true, preparacao: null };
  aviso("Consultando o PNCP…");
  const [compra, itens] = await Promise.all([consultarCompra(c), consultarItens(c)]);
  const id = await gravarLicitacaoDoPncp(c, compra, itens);
  const preparacao = await prepararDoPncp(id, c, aviso);
  // Pasta no OneDrive (D104): falha aqui não desfaz a captação; a ficha tenta de novo ao abrir.
  aviso("Criando a pasta no OneDrive…");
  await sincronizarOneDrive(id).catch(() => null);
  return { id, itens: itens.length, existente: false, preparacao };
}

/**
 * Licitação específica pelo link ou número de controle do PNCP (D70, D110): consulta sozinha, mostra o resumo e
 * cadastra (capta) com os itens e arquivos. Usada em "Nova licitação" e em "Buscar licitações".
 */
export function CadastroPeloPncp({
  titulo = "Preencher pelo PNCP",
  rotuloBotao = "Cadastrar licitação com os itens e arquivos",
  ajuda = "A consulta começa sozinha. Fora do PNCP, preencha o formulário abaixo.",
  textoExterno,
}: {
  titulo?: string;
  rotuloBotao?: string;
  ajuda?: string;
  /** Texto vindo de outro campo (busca livre): o componente não mostra o próprio campo nem o cartão. */
  textoExterno?: string;
}) {
  const notify = useNotify();
  const navigate = useNavigate();
  const [textoProprio, setTexto] = useState("");
  const texto = textoExterno ?? textoProprio;
  const [consultando, setConsultando] = useState(false);
  const [gravando, setGravando] = useState(false);
  const [progresso, setProgresso] = useState("");
  const [resultado, setResultado] = useState<ConsultaPncp | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const controle = useMemo(() => lerControlePncp(texto), [texto]);

  // Consulta sozinha quando o número ou o link fica completo (D46).
  useEffect(() => {
    if (!controle) return;
    let ativo = true;
    setConsultando(true);
    setErro(null);
    setResultado(null);
    void (async () => {
      try {
        const { data: existente } = await supabase.from("licitacao").select("id").eq("numero_controle_pncp", controle.numero).maybeSingle();
        if (existente) {
          notify("Esta licitação já está cadastrada. Abrindo a ficha.", { type: "info" });
          navigate(`/licitacao/${existente.id as string}`);
          return;
        }
        const [compra, itens] = await Promise.all([consultarCompra(controle), consultarItens(controle)]);
        if (ativo) setResultado({ controle, compra, itens });
      } catch (e) {
        if (ativo) setErro((e as Error).message);
      } finally {
        if (ativo) setConsultando(false);
      }
    })();
    return () => {
      ativo = false;
    };
  }, [controle?.numero]);

  async function cadastrar() {
    if (!resultado) return;
    setGravando(true);
    try {
      const id = await gravarLicitacaoDoPncp(resultado.controle, resultado.compra, resultado.itens);
      const preparacao = await prepararDoPncp(id, resultado.controle, setProgresso);
      // Pasta no OneDrive (D104), como na captação pela busca; falha aqui não desfaz o cadastro.
      setProgresso("Criando a pasta no OneDrive…");
      await sincronizarOneDrive(id).catch(() => null);
      notify(
        `Licitação cadastrada com ${resultado.itens.length} item(ns).${preparacao ? ` ${textoDaPreparacao(preparacao)}.` : ""} Confira a data da sessão e os prazos.`,
        { type: "success", autoHideDuration: 12000 },
      );
      navigate(`/licitacao/${id}`);
    } catch (e) {
      notify((e as Error).message, { type: "error" });
    } finally {
      setGravando(false);
    }
  }

  const campo = textoExterno === undefined && (
    <TextFieldMui
      value={texto}
      onChange={(e) => setTexto(e.target.value)}
      placeholder="Cole o link do edital no PNCP ou o número de controle (ex.: 10825373000155-1-000177/2025)"
      fullWidth
      size="small"
      slotProps={{ input: { startAdornment: <SearchIcon sx={{ mr: 1, color: "text.secondary" }} /> } }}
      helperText={texto && !controle ? "Não reconheci um número de controle do PNCP neste texto." : ajuda}
      error={Boolean(texto && !controle)}
    />
  );
  const conteudo = (
    <>
      {consultando && (
        <Typography variant="body2" sx={{ mt: 1 }}>
          Consultando o PNCP…
        </Typography>
      )}
      {erro && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {erro}
        </Alert>
      )}
      {resultado && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2">{resultado.compra.orgaoEntidade.razaoSocial}</Typography>
          <Typography variant="body2" color="text.secondary">
            {resultado.compra.unidadeOrgao.codigoUnidade} — {resultado.compra.unidadeOrgao.nomeUnidade} ({resultado.compra.unidadeOrgao.municipioNome}/
            {resultado.compra.unidadeOrgao.ufSigla})
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {resultado.compra.modalidadeNome} {resultado.compra.numeroCompra}/{resultado.compra.anoCompra}
            {resultado.compra.srp ? " · Registro de preços" : ""} · {resultado.itens.length} item(ns) · propostas até{" "}
            {formatarDataHora(dataHoraPncp(resultado.compra.dataEncerramentoProposta))}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {resultado.compra.objetoCompra}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            Situação no PNCP: {resultado.compra.situacaoCompraNome}
          </Typography>
          <Button variant="contained" sx={{ mt: 2 }} onClick={() => void cadastrar()} disabled={gravando}>
            {gravando ? progresso || "Cadastrando…" : rotuloBotao}
          </Button>
          {linkPncp(resultado.controle.numero) && (
            <Link href={linkPncp(resultado.controle.numero) ?? undefined} target="_blank" rel="noopener noreferrer" variant="body2" sx={{ ml: 2 }}>
              Ver no PNCP
            </Link>
          )}
        </Box>
      )}
    </>
  );
  if (textoExterno !== undefined) return conteudo;
  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="h6" sx={{ fontSize: "1rem", mb: 1 }}>
          {titulo}
        </Typography>
        {campo}
        {conteudo}
      </CardContent>
    </Card>
  );
}

export function LicitacaoCriar() {
  return (
    <Box>
      <Title title="Nova licitação" />
      <Box sx={{ mt: 2 }}>
        <CadastroPeloPncp />
      </Box>
      <Create title=" " redirect="edit" sx={{ "& .RaCreate-main": { mt: 0 } }}>
        <SimpleForm defaultValues={{ srp: false, margem_preferencia: false, carona_limite_aderente_pct: 50, carona_limite_total_pct: 200 }}>
          <Typography variant="h6" sx={{ fontSize: "1rem", mb: 1 }}>
            Cadastro manual
          </Typography>
          <CamposLicitacao />
        </SimpleForm>
      </Create>
    </Box>
  );
}

/* ------------------------------------ Status e linha do tempo ------------------------------------ */

interface Evento {
  id: string;
  de_status: string | null;
  para_status: string;
  observacao: string | null;
  criado_em: string;
  criado_por: string | null;
}

function LinhaDoTempo({ licitacaoId }: { licitacaoId: string }) {
  const { data: eventos = [] } = useGetList<Evento>("licitacao_evento", {
    filter: { licitacao_id: licitacaoId },
    pagination: { page: 1, perPage: 500 },
    sort: { field: "criado_em", order: "DESC" },
  });
  const { data: usuarios = [] } = useGetList<{ id: string; nome: string }>("usuario", {
    pagination: { page: 1, perPage: 200 },
    sort: { field: "nome", order: "ASC" },
  });
  const nome = (id: string | null) => usuarios.find((u) => u.id === id)?.nome ?? "Sistema";
  return (
    <Stack spacing={1.5}>
      {eventos.map((e) => (
        <Box key={e.id} sx={{ borderLeft: 3, borderColor: "secondary.main", pl: 2 }}>
          <Typography variant="body2" fontWeight={600}>
            {e.de_status ? `${nomeStatus(e.de_status)} → ${nomeStatus(e.para_status)}` : nomeStatus(e.para_status)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatarDataHora(e.criado_em)} · {nome(e.criado_por)}
          </Typography>
          {e.observacao && <Typography variant="body2">{e.observacao}</Typography>}
        </Box>
      ))}
    </Stack>
  );
}

/* ------------------------------------ Ficha ------------------------------------ */

interface Licitacao extends LicitacaoPreco {
  id: string;
  pessoa_id: string;
  numero: string;
  objeto: string;
  status: string;
  motivo_descarte: string | null;
  numero_controle_pncp: string | null;
  link_sistema_origem: string | null;
  atualizado_em: string;
  srp: boolean;
  esfera: string | null;
  data_hora_sessao: string | null;
  prazo_entrega_dias: number | null;
  prazo_pagamento_dias: number | null;
  vigencia_ata_meses: number | null;
  prazo_impugnacao: string | null;
  prazo_esclarecimento: string | null;
  uf_destino: string | null;
  margem_preferencia: boolean;
  processo: string | null;
  pasta_url: string | null;
  modalidade_id: string | null;
}

function ConteudoFicha() {
  const { record, isPending } = useEditContext<Licitacao>();
  const [aba, setAba] = useState(0);
  const { data: orgao } = useGetOne<{ id: string; nome: string }>("pessoa", { id: record?.pessoa_id ?? "" }, { enabled: Boolean(record?.pessoa_id) });
  if (isPending || !record) return <Loading />;

  return (
    <Box sx={{ mt: 2 }}>
      <Title title={`Licitação ${record.numero}${orgao ? ` — ${orgao.nome}` : ""}`} />
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} justifyContent="space-between" alignItems={{ md: "center" }}>
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <SeletorStatus licitacaoId={record.id} status={record.status} grande />
                <SelosLicitacao licitacao={record} />
                <AcaoDaEtapa licitacaoId={record.id} status={record.status} />
                <Typography variant="subtitle1" fontWeight={700}>
                  {record.numero}
                </Typography>
                {orgao && (
                  <Typography variant="body2" color="text.secondary">
                    {orgao.nome}
                  </Typography>
                )}
              </Stack>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {record.objeto}
              </Typography>
              {record.status === "descartada" && record.motivo_descarte && (
                <Typography variant="caption" color="text.secondary">
                  Motivo do descarte: {record.motivo_descarte}
                </Typography>
              )}
              <Stack direction="row" spacing={2} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                {record.numero_controle_pncp && linkPncp(record.numero_controle_pncp) && (
                  <Link href={linkPncp(record.numero_controle_pncp) ?? undefined} target="_blank" rel="noopener noreferrer" variant="caption">
                    Ver no PNCP
                  </Link>
                )}
                <PastaOneDrive
                  licitacaoId={record.id}
                  status={record.status}
                  chave={[record.numero, record.data_hora_sessao, record.pessoa_id, record.modalidade_id].join("|")}
                  url={record.pasta_url ?? null}
                />
                {record.link_sistema_origem && (
                  <Link href={record.link_sistema_origem} target="_blank" rel="noopener noreferrer" variant="caption">
                    Abrir no portal de origem
                  </Link>
                )}
              </Stack>
            </Box>
          </Stack>
        </CardContent>
      </Card>
      <Tabs value={aba} onChange={(_, v: number) => setAba(v)} variant="scrollable" allowScrollButtonsMobile sx={{ mb: 1 }}>
        <Tab label="Dados" />
        <Tab label="Itens" />
        <Tab label="Precificação" />
        <Tab label="Disputa" />
        <Tab label="Proposta" />
        <Tab label="Arquivos" />
        <Tab label="Linha do tempo" />
      </Tabs>
      {aba === 0 && (
        <Card>
          <SimpleForm toolbar={<BarraFormulario />}>
            <CamposLicitacao />
          </SimpleForm>
        </Card>
      )}
      {aba === 1 && (
        <Card>
          <CardContent>
            <ItensLicitacao licitacaoId={record.id} pessoaId={record.pessoa_id} />
          </CardContent>
        </Card>
      )}
      {aba === 2 && (
        <Card>
          <CardContent>
            <PrecificacaoLicitacao licitacao={record} />
          </CardContent>
        </Card>
      )}
      {aba === 4 && (
        <Card>
          <CardContent>
            <PropostaLicitacao licitacao={record} />
          </CardContent>
        </Card>
      )}
      {aba === 3 && (
        <Card>
          <CardContent>
            <DisputaLicitacao licitacao={record} status={record.status} />
          </CardContent>
        </Card>
      )}
      {aba === 5 && (
        <Card>
          <CardContent>
            <ArquivosLicitacao licitacao={record} numeroControlePncp={record.numero_controle_pncp} />
          </CardContent>
        </Card>
      )}
      {aba === 6 && (
        <Card>
          <CardContent>
            <LinhaDoTempo licitacaoId={record.id} />
          </CardContent>
        </Card>
      )}
    </Box>
  );
}

/** Colunas que o formulário grava (status e motivo do descarte mudam só por mudar_status_licitacao). */
const COLUNAS_EDITAVEIS = [
  "pessoa_id",
  "unidade_compradora_id",
  "portal_id",
  "numero",
  "processo",
  "modalidade_id",
  "srp",
  "objeto",
  "uf_destino",
  "data_hora_sessao",
  "prazo_impugnacao",
  "prazo_esclarecimento",
  "vigencia_ata_meses",
  "prazo_entrega_dias",
  "prazo_pagamento_dias",
  "esfera",
  "carona_limite_aderente_pct",
  "carona_limite_total_pct",
  "margem_preferencia",
  "responsavel_id",
  "pasta_url",
  "observacao",
] as const;

const somenteEditaveis = (dados: Record<string, unknown>) => Object.fromEntries(COLUNAS_EDITAVEIS.filter((c) => c in dados).map((c) => [c, dados[c]]));

export function LicitacaoEditar() {
  return (
    <Edit mutationMode="pessimistic" redirect={false} component="div" title=" " actions={false} transform={somenteEditaveis}>
      <ConteudoFicha />
    </Edit>
  );
}
